import type { CameraControlRequest, ViewEndpoint } from "../protocol/events";

/** The scanner owns the light; the button reflects the camera's confirmed state. */
export function mountTorch(
  button: HTMLButtonElement,
  endpoint: ViewEndpoint,
  say: (message: string) => void,
): void {
  let scanning = false;
  let supported = false;
  let enabled: boolean | null = false;
  let pending: CameraControlRequest | null = null;

  const render = (): void => {
    button.hidden = !scanning || !supported;
    button.disabled = button.hidden || pending !== null;
    button.setAttribute("aria-pressed", enabled === null ? "mixed" : String(enabled));
    button.setAttribute("aria-busy", String(pending !== null));
    button.title = pending
      ? "Changing torch…"
      : enabled === null
        ? "Torch state unknown. Turn torch off"
        : enabled
          ? "Turn torch off"
          : "Turn torch on";
  };
  const reset = (active: boolean): void => {
    scanning = active;
    supported = false;
    enabled = false;
    pending = null;
    render();
  };

  button.onclick = () => {
    if (!scanning || !supported || pending) return;
    pending = { torch: enabled === false };
    render();
    endpoint.emit("camera:control", pending);
  };
  endpoint.on("session:started", () => reset(true));
  endpoint.on("session:stopped", () => reset(false));
  endpoint.on("camera:failed", () => reset(false));
  endpoint.on("camera:capabilities", ({ capabilities, settings }) => {
    if (!scanning) return;
    const torch = capabilities.torch;
    supported = torch === true || (Array.isArray(torch) && torch.some((value) => value === true));
    if (typeof settings.torch === "boolean") enabled = settings.torch;
    render();
  });
  endpoint.on("camera:applied", ({ request, settings }) => {
    if (!scanning) return;
    // The typed link preserves the request object. An acknowledgement from a camera
    // that has since stopped must never change this session's light or pending state.
    if (request.torch !== undefined && request !== pending) return;
    const ownRequest = request === pending;
    if (typeof settings.torch === "boolean") enabled = settings.torch;
    else if (ownRequest) enabled = null;
    if (ownRequest) {
      pending = null;
      if (enabled === null)
        say("Torch state could not be confirmed. Tap the torch to switch it off.");
      else if (enabled !== request.torch)
        say(
          request.torch
            ? "The camera could not turn the torch on."
            : "The camera could not turn the torch off.",
        );
    }
    render();
  });
  render();
}
